import {
  JoinResult,
  ParticipationUrlInfo,
  ProjectEntryResult,
  ProjectRegisterResult,
} from "@/components/examinee/participation/Types";
import { getApiServerUrl, getAxios, useExamineeAxios } from "@/utils/axios";
import { useMemo } from "react";

/**
 * プロジェクト固有 URL の情報取得 API（認証不要）
 */
export function getProjectParticipationInfo(urlId: string) {
  return getAxios(getApiServerUrl()).get<ParticipationUrlInfo>(
    `/api/participation/project/${urlId}/`
  );
}

/**
 * プロジェクト URL アカウント登録（1 段階目）: Cognito サインアップ
 * 確認コードが入力メールアドレスに送信される。
 * 姓（last_name）・名（first_name）・生年月日（birthday, YYYY-MM-DD）はここで
 * fail-fast 検証され、事前 CSV 登録済みレコードと一致しない場合は
 * Cognito アカウントを作らずに 400 が返る。
 */
export function postProjectEntry(
  email: string,
  password: string,
  urlId: string,
  lastName: string,
  firstName: string,
  birthday: string
) {
  return getAxios(getApiServerUrl()).post<ProjectEntryResult>(
    "/api/participation/project_entry/",
    {
      email,
      password,
      url_id: urlId,
      last_name: lastName,
      first_name: firstName,
      birthday,
    }
  );
}

/**
 * プロジェクト URL アカウント登録（2 段階目）: 確認コード検証＋本登録
 * 姓・名・生年月日で事前 CSV 登録済みレコードと照合・紐付けし、
 * 成功するとログイン状態（AuthenticationResult）が返る。
 * 保存される氏名・カナ・生年月日は照合済みの事前レコードの値（入力値は照合のみに使う）。
 */
export function putProjectRegister(
  entryId: string,
  params: {
    email: string;
    password: string;
    cognito_verification_code: string;
    url_id: string;
    last_name: string;
    first_name: string;
    birthday: string;
  }
) {
  return getAxios(getApiServerUrl()).put<ProjectRegisterResult>(
    `/api/participation/project_entry/${entryId}/`,
    params
  );
}

/**
 * プロジェクト URL アカウント登録（3 段階目）: 確認コード再送
 */
export function patchProjectResendConfirmationCode(
  entryId: string,
  urlId: string
) {
  return getAxios(getApiServerUrl()).patch(
    `/api/participation/project_entry/${entryId}/`,
    { url_id: urlId }
  );
}

/**
 * ログイン済み受診者の参加 API（プロジェクト URL）
 */
export function useParticipationJoinAPI() {
  const axios = useExamineeAxios();
  return useMemo(
    () => ({
      /** プロジェクト固有 URL からのログイン参加（姓・名・生年月日で事前レコードと照合） */
      projectJoin: (
        urlId: string,
        lastName: string,
        firstName: string,
        birthday: string
      ) =>
        axios.post<JoinResult>("/api/participation/project_join/", {
          url_id: urlId,
          last_name: lastName,
          first_name: firstName,
          birthday,
        }),
    }),
    [axios]
  );
}
